import {
  parse,
  type OperationDefinitionNode,
  type SelectionNode,
  Kind,
  visit,
  print,
  type VariableDefinitionNode,
  type DocumentNode,
  OperationTypeNode,
  type SelectionSetNode,
  type FragmentDefinitionNode,
} from 'graphql';

import { type AliasMap, type ModifiedResult } from './interfaces';
import {
  ApiError,
  isNotUndefinedOrNull,
  isRecord,
  objectHasOwnPropertyKey,
  safeJsonStringify,
} from '@bizzdesign/sdk-internal';

/**
 * Base interface for GraphQL query structures
 * @interface GraphQLQueryInput
 * @property {Record<string, unknown>} variables - Variables used in the query
 * @property {string} query - The GraphQL query string
 */
interface GraphQLQueryInput {
  variables: Record<string, unknown>;
  query: string;
}

/**
 * Processes a new selection node, handling aliasing and deduplication.
 * @param selection - The selection node to process
 * @param fieldAliasPrefix - Prefix to use for field aliasing
 * @param seenFields - Set of already processed field names
 * @param aliasToOriginalFieldMap - Mapping of aliases to original field names
 * @param existingSelections - Map of existing field selections
 * @returns {SelectionNode | undefined} The processed selection node or undefined if skipped
 */
function processNewSelection(
  selection: SelectionNode,
  fieldAliasPrefix: string,
  seenFields: Set<string>,
  aliasToOriginalFieldMap: AliasMap,
  existingSelections: Map<string, SelectionNode>,
): SelectionNode | undefined {
  if (selection.kind !== Kind.FIELD) {
    return selection;
  }

  const aliasedSelection = selection.name.value.startsWith(fieldAliasPrefix)
    ? selection
    : createAliasedField(selection, fieldAliasPrefix);

  if (aliasedSelection.kind === Kind.FIELD) {
    const newFieldKey =
      aliasedSelection.alias?.value ?? aliasedSelection.name.value;
    if (!seenFields.has(newFieldKey)) {
      seenFields.add(newFieldKey);
      if (aliasedSelection.alias) {
        aliasToOriginalFieldMap[aliasedSelection.alias.value] =
          selection.name.value;
      }

      const existingField = existingSelections.get(selection.name.value);
      if (existingField?.kind === Kind.FIELD && selection.selectionSet) {
        return {
          ...aliasedSelection,
          selectionSet: selection.selectionSet,
        };
      }
      return aliasedSelection;
    }
  }
  return undefined;
}

/**
 * Processes field selections and handles their integration into the merged result.
 * @param mergedSelections - Array to store the merged selection nodes
 * @param seenFields - Set of already processed field names
 * @param fieldAliasPrefix - Prefix to use for field aliasing
 * @param aliasToOriginalFieldMap - Mapping of aliases to original field names
 * @param existingSelections - Map of existing field selections
 */
function processFieldSelections(
  mergedSelections: SelectionNode[],
  seenFields: Set<string>,
  fieldAliasPrefix: string,
  aliasToOriginalFieldMap: AliasMap,
  existingSelections: Map<string, SelectionNode>,
): (selection: SelectionNode, isNewSelection?: boolean) => void {
  return (selection: SelectionNode, isNewSelection = false) => {
    if (selection.kind !== Kind.FIELD) {
      mergedSelections.push(selection);
      return;
    }

    const fieldKey = selection?.alias?.value ?? selection.name.value;
    if (!fieldKey || seenFields.has(fieldKey)) {
      return;
    }

    if (isNewSelection) {
      const processedSelection = processNewSelection(
        selection,
        fieldAliasPrefix,
        seenFields,
        aliasToOriginalFieldMap,
        existingSelections,
      );
      if (processedSelection) {
        mergedSelections.push(processedSelection);
      }
    } else {
      // Process existing selection
      seenFields.add(fieldKey);
      mergedSelections.push(selection);
      existingSelections.set(selection.name.value, selection);
    }
  };
}

/**
 * Combines two GraphQL selection sets while maintaining proper field aliasing.
 * @param baseSelectionSet - The base selection set to merge into
 * @param newSelectionSet - The new selection set to merge
 * @param fieldAliasPrefix - Prefix to use for field aliasing
 * @param aliasToOriginalFieldMap - Mapping of aliases to original field names
 * @returns {SelectionSetNode} The merged selection set
 */
function combineGraphQLSelections(
  baseSelectionSet: SelectionSetNode | undefined,
  newSelectionSet: SelectionSetNode,
  fieldAliasPrefix: string = '',
  aliasToOriginalFieldMap: AliasMap = {},
): SelectionSetNode {
  if (!baseSelectionSet) {
    return {
      ...newSelectionSet,
      selections: newSelectionSet.selections.map((selection) =>
        createAliasedField(selection, fieldAliasPrefix),
      ),
    };
  }

  const existingSelections = new Map<string, SelectionNode>();
  const mergedSelections: SelectionNode[] = [];
  const seenFields = new Set<string>();

  const processFieldSelection = processFieldSelections(
    mergedSelections,
    seenFields,
    fieldAliasPrefix,
    aliasToOriginalFieldMap,
    existingSelections,
  );
  baseSelectionSet.selections.forEach((selection) =>
    processFieldSelection(selection),
  );
  newSelectionSet.selections.forEach((selection) =>
    processFieldSelection(selection, true),
  );

  return {
    kind: Kind.SELECTION_SET,
    selections: mergedSelections,
  };
}

/**
 * Adds a unique alias to a GraphQL field selection to prevent naming conflicts during query merging.
 * Only modifies field selections (Kind.FIELD), leaving other selection types unchanged.
 *
 * @param field - The SelectionNode to be aliased
 * @param fieldAliasPrefix - Prefix to be added to the field name to create a unique alias
 *
 * @returns {SelectionNode} A new SelectionNode with the added alias
 *
 * @example
 * const field = { kind: Kind.FIELD, name: { value: 'user' } };
 * const aliased = createAliasedField(field, 'q0_');
 * // Result: { kind: Kind.FIELD, name: { value: 'user' }, alias: { value: 'q0_user' } }
 */
function createAliasedField(
  field: SelectionNode,
  fieldAliasPrefix: string,
): SelectionNode {
  if (field.kind === Kind.FIELD) {
    return {
      ...field,
      alias: {
        kind: Kind.NAME,
        value: `${fieldAliasPrefix}_${field.name.value}`,
      },
    };
  }
  return field;
}

function renameAndCollectVariables(
  operationNode: OperationDefinitionNode,
  variableCounter: number,
  variableNameMapping: Map<string, string>,
  mergedOperation: Omit<
    OperationDefinitionNode,
    'variableDefinitions' | 'selectionSet'
  > & {
    variableDefinitions: VariableDefinitionNode[];
    selectionSet: SelectionSetNode;
  },
  originalVariables: Record<string, unknown>,
  mergedVariables: Record<string, unknown>,
): number {
  const varDefs = operationNode.variableDefinitions;
  if (isNotUndefinedOrNull(varDefs)) {
    for (const varDef of varDefs) {
      const originalVarName = varDef.variable.name.value;
      const newVarName = `v${variableCounter}`;
      variableCounter++;

      variableNameMapping.set(originalVarName, newVarName);

      mergedOperation.variableDefinitions.push({
        ...varDef,
        variable: {
          ...varDef.variable,
          name: { kind: Kind.NAME, value: newVarName },
        },
      });

      if (originalVariables[originalVarName] !== undefined) {
        mergedVariables[newVarName] = originalVariables[originalVarName];
      }
    }
  }
  return variableCounter;
}

function updateVariableReferencesInSelectionSet(
  operationNode: OperationDefinitionNode,
  variableNameMapping: Map<string, string>,
  fieldAliasPrefix: string,
): SelectionSetNode {
  const updatedSelectionSet: SelectionSetNode = visit(
    operationNode.selectionSet,
    {
      Field(fieldNode) {
        if (fieldNode.arguments) {
          return {
            ...fieldNode,
            arguments: fieldNode.arguments.map((arg) => {
              if (arg.value.kind === Kind.VARIABLE) {
                const originalVarName = arg.value.name.value;
                const newVarName = variableNameMapping.get(originalVarName);
                if (newVarName != null) {
                  return {
                    ...arg,
                    value: {
                      ...arg.value,
                      name: { kind: Kind.NAME, value: newVarName },
                    },
                  };
                }
              }
              return arg;
            }),
          };
        }
        return undefined;
      },
      FragmentSpread(fragmentSpreadNode) {
        const originalFragmentName = fragmentSpreadNode.name.value;
        const newFragmentName = `${fieldAliasPrefix}_${originalFragmentName}`;
        return {
          ...fragmentSpreadNode,
          name: { kind: Kind.NAME, value: newFragmentName },
        };
      },
    },
  );
  return updatedSelectionSet;
}

function updateVariableReferencesAndMergeSelections(
  operationNode: OperationDefinitionNode,
  variableNameMapping: Map<string, string>,
  mergedOperation: Omit<
    OperationDefinitionNode,
    'variableDefinitions' | 'selectionSet'
  > & {
    variableDefinitions: VariableDefinitionNode[];
    selectionSet: SelectionSetNode;
  },
  fieldAliasPrefix: string,
  aliasToOriginalFieldMap: AliasMap,
): void {
  const updatedSelectionSet = updateVariableReferencesInSelectionSet(
    operationNode,
    variableNameMapping,
    fieldAliasPrefix,
  );

  mergedOperation.selectionSet = combineGraphQLSelections(
    mergedOperation.selectionSet,
    updatedSelectionSet,
    fieldAliasPrefix,
    aliasToOriginalFieldMap,
  );
}

/**
 * Combines multiple GraphQL queries into a single optimized query operation.
 * This function handles:
 * - Merging multiple query operations
 * - Variable renaming and deduplication
 * - Selection set merging with proper aliasing
 * - Maintaining a mapping of aliases to original field names
 *
 * @param queries - Array of query objects, each containing a query string and its variables
 *
 * @returns {Object} Combined query information
 * @returns {string} .query - The merged GraphQL query string
 * @returns {Object} .variables - Combined variables object with renamed variables
 * @returns {AliasMap} .aliasMap - Mapping of aliases to original field names
 */
function batchQueriesWithVariables(queries: GraphQLQueryInput[]): {
  query: string;
  variables: Record<string, unknown>;
  aliasMap: AliasMap;
} {
  const combinedOperationNode: Omit<
    OperationDefinitionNode,
    'variableDefinitions' | 'selectionSet'
  > & {
    variableDefinitions: VariableDefinitionNode[];
    selectionSet: SelectionSetNode;
  } = {
    kind: Kind.OPERATION_DEFINITION,
    operation: OperationTypeNode.QUERY,
    name: { kind: Kind.NAME, value: 'CombinedQuery' },
    variableDefinitions: [],
    selectionSet: { kind: Kind.SELECTION_SET, selections: [] },
  };

  const combinedQueryVariable: Record<string, unknown> = {};
  const aliasToOriginalFieldMap: AliasMap = {};
  const collectedFragments: Map<string, FragmentDefinitionNode> = new Map();
  let variableCounter = 1;

  queries.forEach(({ query, variables }, index) => {
    const ast = parse(query);
    const fieldAliasPrefix = `q${index}`;
    const queryVarMap = new Map<string, string>();

    visit(ast, {
      OperationDefinition(node) {
        variableCounter = renameAndCollectVariables(
          node,
          variableCounter,
          queryVarMap,
          combinedOperationNode,
          variables,
          combinedQueryVariable,
        );
        if (isNotUndefinedOrNull(node.selectionSet)) {
          updateVariableReferencesAndMergeSelections(
            node,
            queryVarMap,
            combinedOperationNode,
            fieldAliasPrefix,
            aliasToOriginalFieldMap,
          );
        }
      },
      FragmentDefinition(fragmentNode) {
        const aliasedFragmentName = `${fieldAliasPrefix}_${fragmentNode.name.value}`;
        const aliasedFragmentNode: FragmentDefinitionNode = {
          ...fragmentNode,
          name: { ...fragmentNode.name, value: aliasedFragmentName },
          selectionSet: visit(fragmentNode.selectionSet, {
            FragmentSpread(spreadNode) {
              return {
                ...spreadNode,
                name: {
                  kind: Kind.NAME,
                  value: `${fieldAliasPrefix}_${spreadNode.name.value}`,
                },
              };
            },
          }),
        };
        collectedFragments.set(aliasedFragmentName, aliasedFragmentNode);
      },
    });
  });

  const batchedAST: DocumentNode = {
    kind: Kind.DOCUMENT,
    definitions: [combinedOperationNode, ...collectedFragments.values()],
  };

  return {
    query: print(batchedAST),
    variables: combinedQueryVariable,
    aliasMap: aliasToOriginalFieldMap,
  };
}

/**
 * Transforms an array of GraphQL queries into a single batched query.
 * @param input - JSON string containing an array of GraphQLQueryInput objects
 * @returns {GraphQLQueryInput & { aliasMap: AliasMap }} Object containing:
 *   - query: The combined GraphQL query string
 *   - variables: Merged variables with renamed keys
 *   - aliasMap: Mapping of aliased fields to their original names
 * @throws {Error} If input is not valid JSON or not an array of queries
 * @example
 * const result = batchQueries(JSON.stringify([
 *   {
 *     query: 'query GetUser($id: ID!) { user(id: $id) { name } }',
 *     variables: { id: "123" }
 *   },
 *   {
 *     query: 'query GetPost($id: ID!) { post(id: $id) { title } }',
 *     variables: { id: "456" }
 *   }
 * ]));
 * // Result:
 * // {
 * //   query: 'query CombinedQuery($v1: ID!, $v2: ID!) { q0_user(id: $v1) { name } q1_post(id: $v2) { title } }',
 * //   variables: { v1: "123", v2: "456" },
 * //   aliasMap: { q0_user: "user", q1_post: "post" }
 * // }
 */
export function batchQueries(
  input: string,
): GraphQLQueryInput & { aliasMap: AliasMap } {
  let queryObjects: GraphQLQueryInput[];
  try {
    queryObjects = JSON.parse(input);
    if (!Array.isArray(queryObjects)) {
      throw new Error('Original queries are not an array.');
    }
  } catch (error) {
    throw new Error('Invalid input: Failed to parse JSON.');
  }

  const combined = batchQueriesWithVariables(queryObjects);

  return {
    variables: combined.variables,
    query: combined.query,
    aliasMap: combined.aliasMap,
  };
}

type BatchedGraphQLError = Record<string, unknown> & {
  path?: Array<string | number>;
};

function isGraphQLErrorPath(path: unknown): path is Array<string | number> {
  return (
    Array.isArray(path) &&
    path.every(
      (segment) => typeof segment === 'string' || typeof segment === 'number',
    )
  );
}

function isBatchedGraphQLError(value: unknown): value is BatchedGraphQLError {
  if (!isRecord(value)) {
    return false;
  }
  const path = value['path'];
  return !isNotUndefinedOrNull(path) || isGraphQLErrorPath(path);
}

function hasOwnAlias(
  aliasToOriginalFieldMap: AliasMap,
  alias: string,
): boolean {
  return objectHasOwnPropertyKey(aliasToOriginalFieldMap, alias);
}

function getErrorAlias(error: BatchedGraphQLError): string | undefined {
  const path = error['path'];
  if (!Array.isArray(path) || path.length === 0) {
    return undefined;
  }
  const firstPathNode = path[0];
  return typeof firstPathNode === 'string' ? firstPathNode : undefined;
}

function rewriteErrorPathAliasToFieldName(
  error: BatchedGraphQLError,
  originalFieldName: string,
): BatchedGraphQLError {
  const path = error['path'];
  if (!Array.isArray(path) || path.length === 0) {
    return error;
  }
  return {
    ...error,
    path: [originalFieldName, ...path.slice(1)],
  };
}

function createBatchRequestFailureError(
  originalFieldName: string,
  unscopedErrors: BatchedGraphQLError[],
): BatchedGraphQLError {
  return {
    message: `No data returned for '${originalFieldName}' because the combined batched request failed.`,
    path: [originalFieldName],
    errorType: 'CombinedBatchRequestFailed',
    extensions: {
      unscopedErrors: [...unscopedErrors],
    },
  };
}

/**
 * Processes a GraphQL response by transforming aliased fields back to their original names.
 * @param response - The raw Response object from the GraphQL request
 * @param aliasToOriginalFieldMap - Mapping of aliases to their original field names
 * @returns {Promise<ModifiedResult[]>} Array of individual query results with restored field names
 * @example
 * const results = await unbatchResults(response, { q0_user: "user", q1_post: "post" });
 * // Results: [
 * //   { data: { user: { name: "John" } } },
 * //   { data: { post: { title: "Hello" } } }
 * // ]
 */
export async function unbatchResults(
  response: Response,
  aliasToOriginalFieldMap: AliasMap,
): Promise<ModifiedResult[]> {
  const responseJson = await response.json();
  if (!isRecord(responseJson)) {
    throw new ApiError(
      `Errors while processing batched response: ${JSON.stringify(responseJson)}`,
    );
  }
  const aliasOrder = Object.keys(aliasToOriginalFieldMap).filter(
    (alias) => aliasToOriginalFieldMap[alias] !== '__typename',
  );
  const responseData = responseJson['data'];
  const data = isRecord(responseData) ? responseData : {};
  const responseErrors = responseJson['errors'];
  const errorsByAlias = new Map<string, BatchedGraphQLError[]>();
  const unscopedErrors: BatchedGraphQLError[] = [];
  let hasBatchErrors = false;
  if (isNotUndefinedOrNull(responseErrors)) {
    const errorsStringifyFallback = '[unserializable context]';
    if (!Array.isArray(responseErrors)) {
      throw new ApiError(
        `Errors while processing batched response: ${safeJsonStringify(responseErrors, errorsStringifyFallback)}`,
      );
    }
    hasBatchErrors = responseErrors.length > 0;
    for (const error of responseErrors) {
      if (!isBatchedGraphQLError(error)) {
        throw new ApiError(
          `Errors while processing batched response: ${safeJsonStringify(responseErrors, errorsStringifyFallback)}`,
        );
      }
      const errorAlias = getErrorAlias(error);
      if (
        !isNotUndefinedOrNull(errorAlias) ||
        !hasOwnAlias(aliasToOriginalFieldMap, errorAlias)
      ) {
        unscopedErrors.push(error);
        continue;
      }
      const originalFieldName = aliasToOriginalFieldMap[errorAlias];
      const errorList = errorsByAlias.get(errorAlias) ?? [];
      errorList.push(
        rewriteErrorPathAliasToFieldName(error, originalFieldName),
      );
      errorsByAlias.set(errorAlias, errorList);
    }
  }
  return aliasOrder.map((alias) => {
    const originalFieldName = aliasToOriginalFieldMap[alias];
    const aliasErrors = errorsByAlias.get(alias) ?? [];
    const hasData = objectHasOwnPropertyKey(data, alias);
    const fallbackErrors =
      !hasData && aliasErrors.length === 0 && hasBatchErrors
        ? [createBatchRequestFailureError(originalFieldName, unscopedErrors)]
        : [];
    const queryErrors = [...aliasErrors, ...fallbackErrors];
    const invalidBackendPayload = !hasData && queryErrors.length === 0;
    if (invalidBackendPayload) {
      throw new ApiError(
        `Malformed batched response: alias '${alias}' contains neither data nor errors.`,
      );
    }
    if (hasData) {
      const result: ModifiedResult = {
        data: {
          [originalFieldName]: data[alias],
        },
      };
      if (queryErrors.length > 0) {
        result.errors = queryErrors;
      }
      return result;
    }
    return { errors: queryErrors } satisfies ModifiedResult;
  });
}
